<template>
    <div class="register-schedule-tour-ads-container">
        <div class="progress-bar">
            <span class="done-step" :style="{ width: '90%' }"></span>
            <span class="rest-step" :style="{ width: '10%' }"></span>
        </div>
        <h2 class="page-title">{{ $t('md-detail-info.scheduleTourAdsTitle') }}</h2>
        <div class="listing-info">
            <img-box class="info-img" :src="previewPicture" :alt="listingDetail?.info?.address || 'Property image'" />
            <div class="info-address">
                {{ listingDetail?.info?.address }}
            </div>
            <div class="house-basic" v-if="basic.length > 0">
                <span class="vertical-line">{{ price }}</span>
                <span
                    class="vertical-line"
                    v-for="item in basic"
                    :key="item.key"
                >{{ item.text }}</span>
            </div>
            <div class="booking-date-wrap">
                <span
                    class="iconfont icon-arrow_08_left booking-prev disabled"
                ></span>
                <Swiper
                    class="booking-date-content swiper-container"
                    v-bind="swiperOption"
                    @init="onSwiperInit"
                >
                    <SwiperSlide
                        v-for="(date, index) in dayList"
                        :key="date.time"
                        class="date"
                        :class="{
                            selected: selectedDateIndex == index,
                            disabled: !date.available.length
                        }"
                        @click="onDayChange(date, index)"
                    >
                        <div class="week">{{ date.week }}</div>
                        <div class="date-day">{{ date.day }}</div>
                        <div class="month">{{ date.month }}</div>
                    </SwiperSlide>
                </Swiper>
                <span class="iconfont icon-arrow_08_right booking-next"></span>
            </div>
            <div class="booking-time schedule-tour-ads-time">
                <p>{{ $t('md-detail-info.tourStartTime') }}</p>
                <Select
                    class="start-time"
                    v-model="timeRange.start"
                    @change="onStartChange()"
                    :options="startList"
                    valueKey="id"
                    labelKey="name"
                    :placeholder="$t('md-detail-info.pleaseSelectStartTime')"
                />
                <p>{{ $t('md-detail-info.tourEndTime') }}</p>
                <Select
                    class="end-time"
                    v-model="timeRange.duration"
                    @change="onEndChange()"
                    :options="endList"
                    valueKey="id"
                    labelKey="name"
                    :placeholder="$t('md-detail-info.pleaseSelectEndTime')"
                />
            </div>
            <div class="btn-wrap">
                <button class="btn-confirm" @click="handleConfirm">{{ $t('md-detail-info.wantATour') }}</button>
                <button class="btn-skip" @click="handleSkip">{{ $t('md-detail-info.later') }}</button>
            </div>
            <div class="schedule-tour-ads-tips" v-html="$t('md-detail-info.scheduleTourAdsTips', { agentName: listingDetail?.agentInfo?.fullName })"></div>
        </div>
    </div>
</template>

<script>
import { Swiper, SwiperSlide, fixSwiperOptions } from '@/common/swiper';
import Select from '@/modules/md-detail-info/_components/select.vue';
import { fifteenMin, getStartList, getEndList, getTodayStartTime, calculateDays, oneDay } from '@/modules/md-detail-info/schedule-appointment.js';
import util from '@/common/util';
import { getBasic } from '@/common/house';
import { mapState as rootMapState } from 'pinia';
import { usePageStore, useUserStore } from '@/store';

export default {
    i18next: {
        ns: 'md-detail-info'
    },
    components: { Swiper, SwiperSlide, Select },
    data: function() {
        return {
            swiper: null,
            restrictTimeList: [],
            selectedDateIndex: 0,
            listingDetail: {},
            showingConfig: {
                durationType: 30
            },
            dayList: [],
            day: null,
            timeRange: {
                start: 0, //timestamp
                duration: 0, // (endtimestamp - starttimestamp )/15min
                right: 0, // right threshold of this available time period
                maxLength: 0 // durationType / 15min
            },
            defaultImg:
                'https://fs01.chime.me/image/fs/sitebuild/2021224/20/original_ddff9285-b8a4-4fd5-840f-0233eb9cc979.png',
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            "currencySymbol": store => store.currency?.symbol
        }),
        ...rootMapState(useUserStore, {
            user: store => store.user
        }),
        swiperOption({ swiperConfig = {} }) {
            let { slidesPerView = 3 } = swiperConfig;
            let options = {
                slidesPerView,
                spaceBetween: 10,
                slidesPerGroup: slidesPerView,
                navigation: {
                    closest: '.booking-date-wrap',
                    prevEl: '.booking-prev',
                    nextEl: '.booking-next',
                    disabledClass: 'disabled'
                }
            };
            Object.assign(options, swiperConfig);
            return fixSwiperOptions(options);
        },
        startList({ day }) {
            return getStartList(day);
        },
        endList({ timeRange }) {
            let ret = getEndList(timeRange);
            return ret;
        },
        basic() {
            return getBasic.call(this, this.listingDetail?.info || {});
        },
        price({ currencySymbol }) {
            return this.$ft.formatCurrency({ value: this.listingDetail?.info?.price, currencySymbol });
        },
        previewPicture() {
            return this.listingDetail?.info?.previewPicture || this.listingDetail?.info?.pictureList?.[0] || this.defaultImg;
        }
    },
    async mounted() {
        const listingDetail = window.siteAppVm?.getModuleByName('md-detail-info');
        if (listingDetail) {
            this.listingDetail = listingDetail;
        }
        await this.queryRestrictTimeList();
        await this.queryShowingConfig();
        this.updateDayList();
        this.init();
    },
    methods: {  
        async handleConfirm() {
            let { start: startTime, duration } = this.timeRange;
            let endTime = startTime + duration * fifteenMin;
            const params = {
                fullName: this.user.fullName,
                phoneNum: this.user.phoneNum,
                account: this.user.account,
                recordFlag: 'tour_info',
                emailAddress: this.user.emailAddress || this.user.account,
                appointmentStartTime: startTime,
                appointmentEndTime: endTime,
                listingId: this.listingDetail?.info.id,
                question: '',
                timeZone: this.getCookie('timezone') || util.getTimeZone()
            };
            // await this.axiosIns.post('/api-site/record/visitorInfo', {
            //     fullName: this.user.fullName,
            //     phoneNum: this.user.phoneNum,
            //     account: this.user.account,
            //     recordFlag: 'tour_info',
            //     emailAddress: this.user.emailAddress || this.user.account,
            //     appointmentStartTime: startTime,
            //     appointmentEndTime: endTime,
            //     listingId: this.listingDetail?.info.id,
            //     question: '',
            //     timeZone: this.getCookie('timezone') || util.getTimeZone()
            // });
            this.$emit('handleNextStep', { scheduleTourParams: params });
        },
        handleSkip() {
            this.$emit('handleNextStep');
        },
        onSwiperInit(swiper) {
            this.swiper = swiper;
        },
        onSlideChange(index) {
            if (!util.isSSRServer && this.swiper) {
                this.swiper.slideTo(index);
            }
        },
        init() {
            this.timeRange.maxLength =
                this.showingConfig.durationType / 15 || 2;
            this.chooseFirstAvailablePeriod(this.selectedDateIndex);
        },
        chooseFirstAvailablePeriod(idx = 0) {
            for (let i = idx; i < this.dayList.length; i++) {
                const day = this.dayList[i];
                if (!day.available.length) {
                    continue;
                }
                this.onDayChange(day, i);
                break;
            }
        },
        onDayChange(day, index) {
            if (!day.available.length || day == this.day) {
                return;
            }
            this.day = day;
            this.onStartChange(day.available[0][0]);
            this.selectedDateIndex = index;
            this.onSlideChange(index);
        },
        async queryRestrictTimeList() {
            let startTime = getTodayStartTime();
            let endTime = startTime + 30 * oneDay;
            let res = await this.axiosIns.get(
                '/api-site/record/queryRestrictTime',
                {
                    params: {
                        listingId: this.listingDetail?.info?.id,
                        startTime,
                        endTime
                    }
                }
            );
            this.restrictTimeList = res.data || [];
        },
        updateDayList() {
            this.dayList = calculateDays({
                $t: this.$t,
                bookedTimeList: this.restrictTimeList,
                beforeShowingType: 0
            });
        },
        async queryShowingConfig() {
            const DEFAULT_DURATION_TYPE = 30;
            try {
                let res = await this.axiosIns.get(
                    '/api-site/record/showing-config',
                    {
                        params: {
                            listingId: this.listingDetail?.info?.id
                        }
                    }
                );
                let { durationType = DEFAULT_DURATION_TYPE } =
                    res.data || {};
                this.showingConfig.durationType = durationType;
            } catch (e) {
                this.showingConfig.durationType = DEFAULT_DURATION_TYPE;
            }
        },
        onStartChange(start) {
            if (!start) {
                start = this.timeRange.start;
            }
            let item = this.startList.find(v => v.id === start);
            let { right } = item;

            this.timeRange.start = start;
            this.timeRange.right = right;
            let duration = Math.min(right, this.timeRange.maxLength, 2);
            this.timeRange.duration = duration;
            this.$emit('change', [start, start + duration * fifteenMin]);
        },
        onEndChange() {
            const start = this.timeRange.start;
            if (!start) {
                return; 
            }
            let right = this.timeRange.duration;
            this.$emit('change', [start, start + right * fifteenMin]);
        },
    }
};
</script>

<style lang="scss" scope>
@import '@/theme/mixin.scss';
.register-schedule-tour-ads-container {
    display: flex;
    flex-direction: column;
    .progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 5px;
        display: flex;
        .done-step {
            height: 100%;
            background-color: var(--g-btn-background);
        }
        .rest-step {
            height: 100%;
            background-color: rgba(218, 218, 218, 0.5);
        }
    }
    h2 {
        flex-shrink: 0;
        font-size: 20px;
        font-weight: 700;
        color: var(--g-primary-color);
        margin-bottom: 20px;
        text-align: center;
        font-family: 'font-Montserrat-700';
    }
    .listing-info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        .info-img {
            width: 100%;
            height: 160px;
            object-fit: cover;
        }
        .info-address {
            margin-top: 15px;
            font-size: 14px;
            color: var(--g-text-color);
            font-weight: 500;
            line-height: 1.2;
            margin-bottom: 5px;
            font-family: 'font-Montserrat-500';
            // 最多显示两行
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .house-basic {
            color: var(--g-tip-color);
            font-size: 12px;
            display: flex;
            flex-wrap: wrap;
            .vertical-line {
                white-space: nowrap;
            }
            .vertical-line:after {
                content: '|';
                margin: 0 7px 0 10px;
            }
            .vertical-line:nth-last-child(1):after {
                content: '';
            }
        }
        .booking-date-wrap {
            display: flex;
            overflow: hidden;
            margin: 20px -5px 20px;
        }
        .booking-prev,
        .booking-next {
            flex-shrink: 0;
            font-size: 14px;
            color: var(--g-primary-color);
            cursor: pointer;
            padding: 0 5px;
            line-height: 94px;
            user-select: none;
            &.disabled {
                opacity: 0.3;
                cursor: not-allowed;
                color: var(--g-primary-color);
            }
            &:hover {
                color: var(--g-btn-background);
                &.disabled {
                    color: var(--g-primary-color);
                }
            }
        }
        .booking-date-content {
            width: 100%;
            overflow: hidden;
        }
        .date {
            height: 94px;
            cursor: pointer;
            color: var(--g-text-color);
            border: 1px solid rgba(218, 218, 218, 0.5);
            padding: 15px 0;
            text-align: center;
            line-height: 1.25;

            &:hover,
            &.selected {
                border-color: var(--g-btn-background);
                background-color: var(--g-btn-background_10);
            }
            &.disabled {
                opacity: 0.4;
                cursor: not-allowed;
            }
            .week {
                font-size: 12px;
            }
            .date-day {
                font-size: 20px;
                color: var(--g-primary-color);
                font-weight: 700;
                margin: 5px 0;
            }
        }

        select {
            font-size: 14px;
            width: 100%;
            height: 40px;
            padding-left: 10px;
            font-family: var(--font-normal);
            box-shadow: none;
            border: 1px solid var(--color-border);
            cursor: pointer;
        }
        .booking-time {
            & > p {
                color: var(--g-primary-color);
                line-height: 1.2;
                margin-bottom: 10px;
                font-weight: 500;
                font-size: 14px;
                &:after {
                    content: '*';
                    color: rgba(240, 69, 76, 1);
                    margin-left: 4px;
                    font-weight: 400;
                }
            }
            .base-select {
                margin-bottom: 20px;
            }
        }
        .btn-wrap {
            display: flex;
            flex-direction: column;
            gap: 17px;
            .btn-confirm {
                height: 40px;
                font-size: 16px;
                font-weight: 500;
                color: var(--g-btn-color);
                background-color: var(--g-btn-background);
                border: none;
                cursor: pointer;
                @include color-button-v2();
                @include color-button-hover();
            }
            .btn-skip {
                height: 40px;
                font-size: 16px;
                font-weight: 500;
                color: var(--g-btn-background);
                border: 1px solid var(--g-btn-background);
                background-color: transparent;
                cursor: pointer;
                &:hover {
                    background-color: rgba(0,0,0,0.1);
                }
            }
        }
        .schedule-tour-ads-tips {
            font-size: 14px;
            color: var(--g-tip-color);
            margin-top: 20px;
            text-align: center;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            b {
                color: var(--g-text-color);
                font-weight: 700;
                font-family: 'font-Montserrat-700';
            }
        }
    }
}
</style>
<style lang="scss">
.schedule-tour-ads-time {
   .base-select {
        .dropdown-menu {
            max-height: 160px;
        }
   }
}
</style>